// src/components/SalesManagersScorecard.jsx
import React, { useState, useMemo } from 'react';
import { MantineReactTable } from 'mantine-react-table';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Group,
  Button,
  Anchor,
  Stack,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import MembershipPopup from '../../sales/MembershipPopup';
import SQLEditorPopup from '../../supply-chain/purchasing/SQLEditorPopup';

// Updated metrics array with modified formatters
const metrics = [
  { key: 'header_totalSales', label: 'Total Sales', isHeader: true },
  { key: 'revenue', label: 'Total Rev', formatter: (v) => `$${v.toLocaleString()}` },
  { key: 'salesVsSellPrice', label: 'Sales vs. Sell Price', formatter: (v) => `${(v).toFixed(1)}%` },

  { key: 'header_salesOrders', label: 'Sales Orders', isHeader: true },
  { key: 'totalBookedSales', label: 'Total Booked', formatter: (v) => `$${v.toLocaleString()}` },
  { key: 'totalBookedKeySales', label: 'Total Booked Key', formatter: (v) => `$${v.toLocaleString()}` },

  { key: 'totalCustomersSold', label: 'Total Customers', formatter: (v) => v },
  { key: 'salesOrderLines', label: '# Sales Lines', formatter: (v) => v },

  { key: 'header_growth', label: 'Growth', isHeader: true },
  { key: 'newCustomerCount', label: '# New Customers', formatter: (v) => v },
  { key: 'percentGrowthCustomers', label: '% Growth Customers', formatter: (v) => `${(v).toFixed(0)}%` },

  { key: 'header_retention', label: 'Retention', isHeader: true },

  { key: 'customersInBook', label: 'Assigned Accounts', formatter: (v) => v },
  { key: 'assignedCustomersSold', label: 'Assigned Purchased', formatter: (v) => v },
  { key: 'assignedCustomersPercentSold', label: 'Assigned Purchased (%)', formatter: (v) => `${(v).toFixed(1)}%` },

  { key: 'keyCustomersInBook', label: 'Assigned Key Accounts', formatter: (v) => v },
  { key: 'assignedKeyCustomersSold', label: 'Assigned Key Purchased', formatter: (v) => v },
  { key: 'assignedKeyCustomersPercentKeySold', label: 'Assigned Key Purchased (%)', formatter: (v) => `${(v).toFixed(1)}%` },

  { key: 'header_productivity', label: 'Productivity', isHeader: true },
  { key: 'quoteDocs', label: 'Avg Quotes per day', formatter: (v) => Math.round(v) },
  { key: 'quoteLines', label: 'RFQs per day', formatter: (v) => Math.round(v) },
  { key: 'callTrackerLines', label: 'Call Trackers—Dev', formatter: (v) => v },

  { key: 'header_proficiency', label: 'Proficiency', isHeader: true },
  { key: 'agedMaterialBookedSales', label: 'Aged Material Booked Sales', formatter: (v) => `$${v.toLocaleString()}` },
];

// MantineReactTable styling overrides - tighter spacing
const mantineTableStyles = {
  table: { border: '1px solid #e0e0e0',   },
  th: { padding: '2px 4px', fontSize: '12px' }, // Very reduced padding
  td: { padding: '1px 4px', fontSize: '11px' }, // Very reduced padding
  root: {
    '& .mantine-Table-td': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontWeight: 600,
   
     
      paddingLeft: '4px',
      minHeight: 'unset',
      height: '22px', // Very small row height
    },
    '& .mantine-Table-tr': {
      cursor: 'pointer',
    },
  },
};

export default function SalesManagersScorecard() {
  // Default from = 7 days ago, to = today
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d;
  });
  const [toDate, setToDate] = useState(() => new Date());

  // Track which period button is active
  const [activePeriod, setActivePeriod] = useState(null);

  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openQuery, setOpenQuery] = useState(false);

  // Helper functions for date range buttons
  const getWeekToDate = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    return {
      from: startOfWeek,
      to: now
    };
  };

  const getMonthToDate = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      from: startOfMonth,
      to: now
    };
  };

  const getQuarterToDate = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
    const startOfQuarter = new Date(now.getFullYear(), quarterStartMonth, 1);
    return {
      from: startOfQuarter,
      to: now
    };
  };

  const getYearToDate = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    return {
      from: startOfYear,
      to: now
    };
  };

  // Handle button clicks
  const handleWTD = () => {
    const { from, to } = getWeekToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('WTD');
  };

  const handleMTD = () => {
    const { from, to } = getMonthToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('MTD');
  };

  const handleQTD = () => {
    const { from, to } = getQuarterToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('QTD');
  };

  const handleYTD = () => {
    const { from, to } = getYearToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('YTD');
  };

  // Custom handler for date picker to unselect period buttons
  const handleDateChange = (newDate, type) => {
    if (type === 'from') {
      setFromDate(newDate);
    } else {
      setToDate(newDate);
    }
    // Unselect period buttons when manual date is changed
    setActivePeriod(null);
  };

  // Format YYYY-MM-DD
  const fromParam = fromDate.toISOString().split('T')[0];
  const toParam = toDate.toISOString().split('T')[0];

  const url = `${config.apiBaseUrl}/SalesManager/Scorecard?from=${fromParam}&to=${toParam}`;
  const { data, isLoading, error } = useAuthenticatedData(url);

  // Build columns + rows
  const { columns, rows, avgJoeCode, otherCode } = useMemo(() => {
    if (!data?.rows) return { columns: [], rows: [], avgJoeCode: null, otherCode: null };

    // Find the Average Joe and Other rows
    const avgJoeCode = data.rows.find((r) => r.isAverageJoe)?.ccode;
    const otherCode = data.rows.find((r) => r.ccode === 'Other')?.ccode || 'Other';

    // Get list of sales codes, exclude avgJoe and Other
    const allCodes = Array.from(
      new Set(data.rows.filter((r) => r.onSalesTeam && r.ccode !== otherCode).map((r) => r.ccode))
    );

    // Sort codes with avgJoe and Other at the end
    const sortedCodes = [
      ...allCodes.filter((c) => c !== avgJoeCode),
      ...(avgJoeCode ? [avgJoeCode] : []),
      otherCode
    ];

    // Columns: metric + one per person
    const cols = [
      {
        accessorKey: 'metric',
        header: 'Metrics',
        size: 160, // Further reduced
        enablePinning: true,
        enableSorting: false,
        enableColumnFilter: false,
      },
      ...sortedCodes.map((code) => ({
        accessorKey: code,
        header: data.rows.find((r) => r.ccode === code)?.name || code,
        size: 100, // Further reduced
        enableSorting: false,
        enableColumnFilter: false,
      })),
    ];

    // Pivot rows
    const pivotRows = metrics.map((m) => {
      const row = {
        id: m.key,
        metric: m.label,
        isHeader: m.isHeader || false
      };

      if (!m.isHeader) {
        sortedCodes.forEach((code) => {
          const rep = data.rows.find((r) => r.ccode === code) || {};
          let raw =
            rep[m.key] ??
            (m.key === 'percentGrowthCustomers' ? rep.percentCustomersSold : null);
          row[code] = raw != null ? (m.formatter ? m.formatter(raw) : raw) : '-';
        });
      } else {
        // For header rows, leave cells empty
        sortedCodes.forEach((code) => {
          row[code] = '';
        });
      }

      return row;
    });

    return { columns: cols, rows: pivotRows, avgJoeCode, otherCode };
  }, [data]);

  return (
    <Paper shadow="md"   radius="md" >
      <Stack gap="xs">
        {/* Title with Orbitron font */}
        <Text
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: '0.5rem',
          }}
          component="h1"
        >
          Sales Managers Scorecard
        </Text>

        {/* Technical options: small links for debugging */}
        <Group gap="xs" mb="xs">
 
            <Anchor
                         size="xs"
                         c="dimmed"
                         onClick={() =>
                             window.open(
                                 '/query-commander/editor?queryKey=sales-managers-scorecard',
                                 '_blank'
                             )
                         }
                         style={{ cursor: 'pointer' }}
                     >
                         Query
                     </Anchor>
        </Group>

        {/* Date range controls - very tight spacing */}
        <Stack gap="xs">
          <Group gap="xs">
            <Button
              onClick={handleWTD}
              variant={activePeriod === 'WTD' ? 'filled' : 'outline'}
              size="xs"
            >
              Week-to-Date
            </Button>
            <Button
              onClick={handleMTD}
              variant={activePeriod === 'MTD' ? 'filled' : 'outline'}
              size="xs"
            >
              Month-to-Date
            </Button>
            <Button
              onClick={handleQTD}
              variant={activePeriod === 'QTD' ? 'filled' : 'outline'}
              size="xs"
            >
              Quarter-to-Date
            </Button>
            <Button
              onClick={handleYTD}
              variant={activePeriod === 'YTD' ? 'filled' : 'outline'}
              size="xs"
            >
              Year-to-Date
            </Button>
          </Group>

          {/* Date pickers - compact */}
          <Group gap="xs">
            <DateInput
              label="From"
              value={fromDate}
              onChange={(newVal) => handleDateChange(newVal, 'from')}
              size="xs"
              style={{ width: '140px' }}
            />
            <DateInput
              label="To"
              value={toDate}
              onChange={(newVal) => handleDateChange(newVal, 'to')}
              size="xs"
              style={{ width: '140px' }}
            />
          </Group>
        </Stack>

        {/* Table container - minimal padding */}
        <Paper withBorder style={{ height: '500px', width: '100%' }}>
          <Box style={{ height: '100%', padding: '4px' }}>
            {isLoading ? (
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Loader size="sm" />
                <Text size="sm" ml="xs">Loading...</Text>
              </Box>
            ) : error ? (
              <Alert color="red" title="Error">
                {error.message}
              </Alert>
            ) : (
              <MantineReactTable
                columns={columns}
                data={rows}
                enablePinning
                initialState={{
                  columnPinning: {
                    left: ['metric'],
                    right: [avgJoeCode, otherCode].filter(Boolean),
                  },
                  pagination: {
                    pageSize: 50,
                  },
                  density: 'xs', // Extra small density
                }}
                enableTopToolbar={false}
                mantineTableProps={{
                
                  highlightOnHover: true,
                  withBorder: true,
                  fontSize: 'xs',
                  styles: mantineTableStyles,
                }}
                mantineTableHeadProps={{
                  style: { height: '24px'  }, // Very small header
                }}
                enableGlobalFilter={false}
                enableColumnFilters={false}
                enableSorting={false}
                enableRowSelection={false}
                getRowId={(row) => row.id}
                mantineTableBodyRowProps={({ row }) => ({
                  style: {
                    cursor: 'pointer',
                    backgroundColor: row.original.isHeader ? '#333333' : undefined,
      
                    fontWeight: row.original.isHeader ? 'bold' : undefined,
                    height: row.original.isHeader ? '24px' : '20px', // Very reduced heights
                  },
                })}
                mantineTableBodyCellProps={({ cell }) => ({
                  style: {
                    backgroundColor: cell.row.original.isHeader ? '#333333' : undefined,
    
                    padding: '1px 3px', // Minimal cell padding
                    fontSize: '10px', // Very small font
                  },
                })}
                enablePagination={false}
                enableExport={false}
              />
            )}
          </Box>
        </Paper>
      </Stack>

      {/* Pop-up dialogs */}
      <MembershipPopup open={openMembership} onClose={() => setOpenMembership(false)} />
      <SQLEditorPopup
        open={openQuery}
        onClose={() => setOpenQuery(false)}
        title="SQL Query"
        endpoint={`${config.apiBaseUrl}/SalesManager/Scorecard/queries/managers-scorecard-query`}
      />
    </Paper>
  );
}